<script setup>
import { ref, computed, onMounted } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import UserService from '@/services/UserService'
import FormButton from '@/components/Atoms/FormButton.vue'
import FloatingInput from '@/components/ui/FloatingInput.vue'
import Alert from '@/components/Atoms/Alert.vue'
import { redirectAfterAuth } from '@/Utils/authRedirect'
import { track, EVENTS } from '@/lib/tracking'

const { notify } = useNotification()

const email = ref('')
const password = ref('')
const showPassword = ref(false)
const processing = ref(false)
const error = ref('')
const done = ref(false)

const canSubmit = computed(
	() => !!email.value.trim() && !!password.value && !processing.value,
)

onMounted(() => {
	document.title = 'Admin login – Castr'
})

async function tryLogin() {
	error.value = ''
	if (!email.value.trim() || !password.value) {
		error.value = 'Email and password are required.'
		return
	}
	processing.value = true
	try {
		const { user, authKey } = await UserService.adminLogin(
			email.value.trim(),
			password.value,
		)
		UserService.setSession(user, authKey)
		// Admin credentials already prove identity and /admin/login returns a full
		// session, so treat it as satisfying 2FA. Otherwise the router guard sees the
		// target account's `twoFactorEnabled` and sends support to the TOTP page,
		// where they can't produce the client's code. Cleared on logout / next login.
		localStorage.setItem('twoFactorValidated', 'true')
		track(EVENTS.ADMIN_LOGIN_COMPLETED, { method: 'admin_password' })
		notify({ group: 'success', text: 'Successfully logged in' })
		done.value = true
		setTimeout(() => redirectAfterAuth(), 800)
	} catch (err) {
		error.value = err?.message || 'Something went wrong. Please try again.'
		processing.value = false
	}
}
</script>

<template>
	<div class="auth-page">
		<div class="auth-card">
			<div class="flex flex-col items-center text-center mb-7">
				<div class="auth-logo-wrapper">
					<div class="auth-logo-dot"></div>
					<img class="auth-logo" src="@/assets/castr.svg" alt="Castr" />
				</div>
			</div>

			<div class="mb-6">
				<div class="auth-badge">Admin</div>
				<h1 class="auth-title">Administration login</h1>
				<p class="auth-subtitle">Sign in with your admin credentials</p>
			</div>

			<Alert v-if="error" type="error" :text="error" class="left inline mb-4" />

			<form @submit.prevent="tryLogin">
				<div class="mb-4">
					<FloatingInput
						id="admin-email"
						label="Email address"
						v-model="email"
						type="email"
						inputmode="email"
						autocomplete="email"
						@update:modelValue="error = ''"
					/>
				</div>

				<div class="mb-1">
					<FloatingInput
						id="admin-password"
						label="Password"
						v-model="password"
						:type="showPassword ? 'text' : 'password'"
						autocomplete="current-password"
						@update:modelValue="error = ''"
						@keydown="$event.key === 'Enter' && tryLogin()"
					/>
				</div>
				<div class="flex justify-end mb-5">
					<button
						type="button"
						class="auth-ghost-btn text-tiny"
						@click="showPassword = !showPassword"
					>
						{{ showPassword ? 'Hide password' : 'Show password' }}
					</button>
				</div>

				<div class="flex justify-center">
					<FormButton
						type="main"
						size="lg"
						class="!h-10 !px-14"
						:disabled="!canSubmit"
						@click="tryLogin"
					>
						{{ processing ? 'Signing in…' : done ? 'Redirecting…' : 'Log in' }}
					</FormButton>
				</div>
			</form>
		</div>
	</div>
</template>

<style scoped>
.auth-page {
	position: relative;
	width: 100%;
	min-height: 100vh;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 16px;
}
/* Ambient brand wash. Sized in percentages and anchored top-centre to match
   the marketing pages (ToolShell, PricingPage); a fixed-pixel circle shrinks
   to a blob on wide displays. Second layer lifts the card off the flat page. */
.auth-page::before {
	content: '';
	position: absolute;
	inset: 0;
	background:
		radial-gradient(65% 45% at 50% 0%, rgba(0, 98, 255, 0.16), transparent 68%),
		radial-gradient(34% 42% at 50% 46%, rgba(0, 98, 255, 0.07), transparent 72%);
	pointer-events: none;
}
.auth-page > * {
	position: relative;
}
.auth-card {
	width: 100%;
	max-width: 384px;
	background-color: var(--castr-surface);
	border: 1px solid var(--castr-border);
	border-radius: 16px;
	box-shadow: 0 24px 60px -24px rgba(0, 0, 0, 0.9);
	padding: 40px 36px 32px;
}
/* Native 120x40 rendering + dot copied verbatim from MainSidebarLogo.vue
   (top-3 left-[116px] size-1) so it matches the dashboard exactly. */
.auth-logo {
	width: 120px;
	height: 40px;
}
.auth-logo-wrapper {
	position: relative;
	display: inline-block;
	line-height: 0;
}
.auth-logo-dot {
	position: absolute;
	width: 4px;
	height: 4px;
	border-radius: 50%;
	left: 116px;
	top: 12px;
	background-color: #ef0000;
	animation: blink 2s ease-in-out infinite;
}
.auth-badge {
	display: inline-block;
	margin: 0 auto 10px;
	padding: 2px 10px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: var(--castr-blue-soft);
	background-color: rgba(0, 98, 255, 0.15);
}
.auth-title {
	font-family: var(--font-display);
	font-size: 24px;
	line-height: 1.15;
	font-weight: 700;
	letter-spacing: -0.02em;
	color: #fff;
	text-align: center;
	margin-bottom: 6px;
}
.auth-subtitle {
	font-size: 14px;
	color: var(--castr-text-muted);
	text-align: center;
}
.auth-ghost-btn {
	color: var(--castr-text-muted);
}
.auth-ghost-btn:hover {
	color: var(--castr-text);
}
.mb-6 {
	text-align: center;
}

/* Primary action, matched to the marketing hero CTA. The radius needs
   !important because FormButton hardcodes `!rounded-md`. */
.auth-card :deep(button.form-button) {
	font-size: 14px;
	font-weight: 600;
	letter-spacing: 0.01em;
	border-radius: 12px !important;
}
.auth-card :deep(button.bg-main:not(:disabled)) {
	box-shadow: 0 10px 30px -8px rgba(0, 98, 255, 0.5);
	transition:
		box-shadow 0.2s ease,
		transform 0.12s ease,
		background-color 0.15s ease;
}
.auth-card :deep(button.bg-main:not(:disabled):hover) {
	transform: translateY(-1px);
	box-shadow: 0 14px 36px -8px rgba(0, 98, 255, 0.6);
}
.auth-card :deep(button.bg-main:not(:disabled):active) {
	transform: translateY(0);
	box-shadow: 0 6px 18px -8px rgba(0, 98, 255, 0.5);
}
.auth-card :deep(button:disabled) {
	background-color: rgba(255, 255, 255, 0.06) !important;
	color: var(--castr-text-muted) !important;
	cursor: not-allowed;
	box-shadow: none;
}
</style>

<style>
/* The auth routes are fullScreen, but #app and the always-mounted AppFooter
   still paint the navy app shell behind the card. Scoped to :has() so the
   override lifts automatically on route change. */
body:has(.auth-page),
body:has(.auth-page) #app {
	background-color: var(--castr-bg);
}
body:has(.auth-page) .footer span {
	color: var(--castr-text-muted);
}
</style>
